import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { MagnifyingGlass, ArrowRight, Globe, Truck, ShieldCheck, Lightning, MapPin, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Reveal, MaskLine } from "@/components/site/Reveal";
import QuoteCalculator from "@/components/site/QuoteCalculator";
import { api, formatApiError } from "@/lib/api";

const HERO_IMG = "https://images.unsplash.com/photo-1612630741022-b29ec17d013d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHxjb3VyaWVyJTIwdmFuJTIwZGVsaXZlcnl8ZW58MHx8fHwxNzg0Nzg2OTAxfDA&ixlib=rb-4.1.0&q=85";
const WAREHOUSE_IMG = "https://images.pexels.com/photos/38195854/pexels-photo-38195854.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WORKER_IMG = "https://images.pexels.com/photos/6869040/pexels-photo-6869040.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const services = [
  { icon: Truck, title: "Domestic Courier", desc: "City-to-city and doorstep delivery across the country with same-day and next-day options." },
  { icon: Globe, title: "International Shipping", desc: "Customs-cleared air & sea freight to 200+ destinations with full compliance handling." },
  { icon: Lightning, title: "Express Priority", desc: "Time-critical parcels moved on the fastest routes with guaranteed delivery windows." },
  { icon: ShieldCheck, title: "Insured & Tracked", desc: "Every shipment insured and traced door-to-door with real-time status updates." },
];

const manifesto = [
  { n: "01", title: "Speed with certainty", text: "We don't just move fast — we move predictably. Every route is optimised so your parcel arrives when we say it will." },
  { n: "02", title: "Borders are logistics, not limits", text: "Domestic lanes and international gateways run on one network. Customs, compliance and last-mile — handled." },
  { n: "03", title: "Visible at every step", text: "From pickup to signature, you see exactly where your parcel is. No black boxes, no guessing." },
];

export default function Home() {
  const navigate = useNavigate();
  const [tracking, setTracking] = useState("");
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);

  const [booking, setBooking] = useState({ name: "", phone: "", email: "", pickup_address: "", delivery_address: "", service_type: "Domestic", weight_kg: 1, preferred_date: "", notes: "" });
  const [bookingRef, setBookingRef] = useState(null);
  const [bLoading, setBLoading] = useState(false);
  const [contact, setContact] = useState({ name: "", email: "", phone: "", message: "" });
  const [cLoading, setCLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);

  const doTrack = (e) => {
    e.preventDefault();
    if (tracking.trim()) navigate(`/track?number=${encodeURIComponent(tracking.trim())}`);
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setBLoading(true);
    try {
      const { data } = await api.post("/bookings", { ...booking, weight_kg: parseFloat(booking.weight_kg) || 1, email: booking.email || null });
      setBookingRef(data.reference);
      toast.success(`Pickup requested! Reference ${data.reference}`);
      setBooking({ name: "", phone: "", email: "", pickup_address: "", delivery_address: "", service_type: "Domestic", weight_kg: 1, preferred_date: "", notes: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBLoading(false);
    }
  };

  const submitContact = async (e) => {
    e.preventDefault();
    setCLoading(true);
    try {
      const { data } = await api.post("/contact", contact);
      toast.success(data.message);
      setContact({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setCLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border-b-2 border-neutral-300 focus:border-signal outline-none py-2 font-sans text-obsidian placeholder:text-neutral-400 transition-colors";

  return (
    <div className="overflow-x-hidden">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end pt-28 pb-16 overflow-hidden">
        <motion.div className="absolute inset-0 z-0" style={{ y: imgY }}>
          <img src={HERO_IMG} alt="Courier delivery van" className="w-full h-[120%] object-cover" />
          <div
            className="absolute inset-0 z-10"
            style={{
              background:
                "linear-gradient(90deg, rgba(250,250,250,0.98) 0%, rgba(250,250,250,0.92) 32%, rgba(250,250,250,0.55) 55%, rgba(250,250,250,0.05) 100%)",
            }}
          />
          <div
            className="absolute inset-0 z-10"
            style={{
              background:
                "linear-gradient(0deg, rgba(250,250,250,0.9) 0%, rgba(250,250,250,0) 40%)",
            }}
          />
        </motion.div>

        <div className="relative z-20 max-w-[1400px] mx-auto px-6 w-full">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-6">
            Domestic · International · Since Day One
          </motion.div>
          <h1 className="font-display font-semibold text-obsidian text-5xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tighter max-w-5xl">
            <MaskLine delay={0.15}>Your parcel,</MaskLine>
            <MaskLine delay={0.28}>delivered like <span className="text-signal">family.</span></MaskLine>
          </h1>

          <motion.form onSubmit={doTrack} initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="mt-12 flex flex-col sm:flex-row gap-0 max-w-2xl bg-white border-2 border-obsidian shadow-brutal" data-testid="hero-track-form">
            <div className="flex items-center gap-3 px-5 flex-1">
              <MagnifyingGlass weight="bold" className="text-neutral-400" size={22} />
              <input data-testid="hero-track-input" value={tracking} onChange={(e) => setTracking(e.target.value)}
                placeholder="Enter tracking number (try MP100001)"
                className="w-full py-4 outline-none font-mono tracking-wide text-obsidian bg-transparent" />
            </div>
            <button data-testid="hero-track-btn" type="submit"
              className="group bg-signal text-white font-medium px-8 py-4 hover:bg-obsidian transition-colors flex items-center gap-2 justify-center">
              Track <ArrowRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
          </motion.form>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-obsidian py-8 border-y-2 border-obsidian">
        <Marquee speed={45} gradient={false}>
          {["DOMESTIC DELIVERY", "AIR FREIGHT", "SEA CARGO", "SAME-DAY EXPRESS", "CUSTOMS CLEARED", "DOOR TO DOOR", "REAL-TIME TRACKING"].map((t, i) => (
            <span key={i} className="font-display font-semibold text-5xl md:text-6xl text-stroke-light mx-8 tracking-tight uppercase">
              {t} <span className="text-signal">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-6 py-28 md:py-36">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">The Manifesto</p>
          <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tighter max-w-3xl leading-[0.95]">
            How we think about moving your things.
          </h2>
        </Reveal>
        <div className="mt-20 space-y-4">
          {manifesto.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.05}>
              <div className="grid md:grid-cols-[240px_1fr] gap-6 md:gap-12 items-start border-t border-neutral-300 pt-8 group">
                <span className="font-display font-semibold text-7xl md:text-8xl text-neutral-200 group-hover:text-signal transition-colors duration-500 tracking-tighter leading-none">
                  {m.n}
                </span>
                <div className="max-w-2xl">
                  <h3 className="font-display font-medium text-2xl md:text-4xl tracking-tight text-obsidian">{m.title}</h3>
                  <p className="font-sans text-neutral-600 text-lg mt-4 leading-relaxed">{m.text}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="bg-obsidian text-stark py-28 md:py-36 scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">What we carry</p>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tighter max-w-3xl leading-[0.95]">
              One network. Every kind of delivery.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-800 mt-16 border border-neutral-800">
            {services.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <div className="bg-obsidian p-8 h-full hover:bg-neutral-900 transition-colors duration-300 group">
                  <s.icon weight="duotone" size={44} className="text-signal group-hover:scale-110 transition-transform duration-300" />
                  <h3 className="font-display font-medium text-2xl mt-6 tracking-tight">{s.title}</h3>
                  <p className="font-sans text-neutral-400 mt-3 leading-relaxed text-sm">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* IMAGE STRIP */}
      <section className="grid md:grid-cols-2">
        {[{ img: WAREHOUSE_IMG, label: "Sorting Hubs", stat: "40+ Facilities" }, { img: WORKER_IMG, label: "Last-Mile Crew", stat: "5000+ Riders" }].map((b, i) => (
          <div key={i} className="relative h-[50vh] overflow-hidden group">
            <img src={b.img} alt={b.label} className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-obsidian/40 group-hover:bg-obsidian/20 transition-colors" />
            <div className="absolute bottom-0 left-0 p-8">
              <p className="font-mono text-xs tracking-widest uppercase text-signal">{b.stat}</p>
              <p className="font-display font-semibold text-3xl text-stark tracking-tight">{b.label}</p>
            </div>
          </div>
        ))}
      </section>

      {/* QUOTE */}
      <section id="quote" className="max-w-[1400px] mx-auto px-6 py-28 md:py-36 scroll-mt-20">
        <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-center">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">Instant pricing</p>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Know the cost before you ship.
            </h2>
            <p className="font-sans text-neutral-600 text-lg mt-6 leading-relaxed max-w-md">
              Transparent rates for domestic and international parcels. Calculate in seconds, then request a pickup below.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <QuoteCalculator />
          </Reveal>
        </div>
      </section>

      {/* BOOKING */}
      <section id="booking" className="bg-neutral-100 py-28 md:py-36 scroll-mt-20 border-y border-neutral-200">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[1fr_1.2fr] gap-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">Schedule a pickup</p>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              We come to your door.
            </h2>
            <p className="font-sans text-neutral-600 text-lg mt-6 leading-relaxed">
              Book a pickup and our rider will collect your parcel. You&apos;ll get a booking reference instantly.
            </p>
            {bookingRef && (
              <div className="mt-8 border-2 border-obsidian bg-white p-6 shadow-brutal-orange" data-testid="booking-ref">
                <p className="font-mono text-xs tracking-widest uppercase text-neutral-500">Booking Reference</p>
                <p className="font-mono text-3xl text-signal tracking-widest">{bookingRef}</p>
              </div>
            )}
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submitBooking} className="bg-white border-2 border-obsidian shadow-brutal p-8 space-y-6" data-testid="booking-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Full Name</label>
                  <input data-testid="booking-name" required className={inputCls} value={booking.name} onChange={(e) => setBooking({ ...booking, name: e.target.value })} /></div>
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Phone</label>
                  <input data-testid="booking-phone" required className={inputCls} value={booking.phone} onChange={(e) => setBooking({ ...booking, phone: e.target.value })} /></div>
              </div>
              <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Pickup Address</label>
                <input data-testid="booking-pickup" required className={inputCls} value={booking.pickup_address} onChange={(e) => setBooking({ ...booking, pickup_address: e.target.value })} /></div>
              <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Delivery Address</label>
                <input data-testid="booking-delivery" required className={inputCls} value={booking.delivery_address} onChange={(e) => setBooking({ ...booking, delivery_address: e.target.value })} /></div>
              <div className="grid sm:grid-cols-3 gap-6">
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Service</label>
                  <select data-testid="booking-service" className={inputCls} value={booking.service_type} onChange={(e) => setBooking({ ...booking, service_type: e.target.value })}>
                    <option>Domestic</option><option>International</option></select></div>
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Weight (kg)</label>
                  <input data-testid="booking-weight" type="number" min="0.1" step="0.1" className={inputCls} value={booking.weight_kg} onChange={(e) => setBooking({ ...booking, weight_kg: e.target.value })} /></div>
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Preferred Date</label>
                  <input data-testid="booking-date" type="date" className={inputCls} value={booking.preferred_date} onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })} /></div>
              </div>
              <button data-testid="booking-submit" disabled={bLoading}
                className="group flex items-center gap-2 bg-obsidian text-white font-medium px-6 py-3 hover:bg-signal transition-colors disabled:opacity-60">
                {bLoading ? "Booking..." : "Request Pickup"} <ArrowRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* BRANCHES */}
      <section id="branches" className="max-w-[1400px] mx-auto px-6 py-28 md:py-36 scroll-mt-20">
        <Reveal>
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">Where we are</p>
          <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tighter leading-[0.95] max-w-2xl">
            Branches & international gateways.
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 mt-16 border border-neutral-200">
          {branches.map((b, i) => (
            <Reveal key={i} delay={(i % 3) * 0.06}>
              <div className="bg-white p-8 h-full hover:bg-neutral-50 transition-colors group" data-testid={`branch-${i}`}>
                <div className="flex items-start justify-between">
                  <MapPin weight="duotone" size={32} className="text-signal" />
                  <span className="font-mono text-[10px] tracking-widest uppercase text-neutral-400 border border-neutral-300 px-2 py-1">{b.type}</span>
                </div>
                <h3 className="font-display font-medium text-2xl mt-5 tracking-tight text-obsidian">{b.city}</h3>
                <p className="font-sans text-neutral-500 text-sm mt-2 leading-relaxed">{b.address}</p>
                <p className="font-mono text-sm text-obsidian mt-3 flex items-center gap-2"><Phone size={14} weight="bold" /> {b.phone}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="bg-obsidian text-stark py-28 md:py-36 scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-2 gap-16">
          <Reveal>
            <p className="font-mono text-xs tracking-[0.3em] uppercase text-signal mb-4">Talk to us</p>
            <h2 className="font-display font-semibold text-4xl md:text-6xl tracking-tighter leading-[0.95]">
              Questions? We&apos;re one call away.
            </h2>
            <a href="tel:9154288291" className="mt-10 inline-flex items-center gap-3 font-display font-semibold text-4xl md:text-5xl text-signal hover:text-white transition-colors" data-testid="contact-phone">
              <Phone weight="fill" size={36} /> 91542 88291
            </a>
            <p className="font-sans text-neutral-400 mt-8 max-w-sm leading-relaxed">
              Prefer email? Drop us a message and our logistics team will respond within one business day.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <form onSubmit={submitContact} className="space-y-6" data-testid="contact-form">
              <div className="grid sm:grid-cols-2 gap-6">
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Name</label>
                  <input data-testid="contact-name" required value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                    className="w-full bg-transparent border-b-2 border-neutral-700 focus:border-signal outline-none py-2 text-stark transition-colors" /></div>
                <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Email</label>
                  <input data-testid="contact-email" type="email" required value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                    className="w-full bg-transparent border-b-2 border-neutral-700 focus:border-signal outline-none py-2 text-stark transition-colors" /></div>
              </div>
              <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Phone (optional)</label>
                <input data-testid="contact-phone-input" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                  className="w-full bg-transparent border-b-2 border-neutral-700 focus:border-signal outline-none py-2 text-stark transition-colors" /></div>
              <div><label className="font-mono text-[11px] tracking-widest uppercase text-neutral-500">Message</label>
                <textarea data-testid="contact-message" required rows={4} value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })}
                  className="w-full bg-transparent border-b-2 border-neutral-700 focus:border-signal outline-none py-2 text-stark resize-none transition-colors" /></div>
              <button data-testid="contact-submit" disabled={cLoading}
                className="group flex items-center gap-2 bg-signal text-white font-medium px-6 py-3 hover:bg-white hover:text-obsidian transition-colors disabled:opacity-60">
                {cLoading ? "Sending..." : "Send Message"} <ArrowRight weight="bold" className="group-hover:translate-x-1 transition-transform" />
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
